/**
 * PATH: packages/email-hub/src/feature/components/SmartActionBar.tsx
 * FILE_ID: EMAILHUB-SMARTACTIONBAR-001
 * PURPOSE: The "verbs" half of the email reader header. Renders the
 *          ONE dominant next action for an email (filled primary) plus
 *          a few supporting actions (outline chips), computed by the
 *          pure `deriveHeaderActions` resolver.
 *
 * WHY:     Operator (2026-06-02) — first-principles design of the
 *          header whitespace: surface easy, context-aware actions
 *          instead of a read-only chip wall. The owner / invite / when
 *          affordances are delivered by the reused `EmailAllocationRail`
 *          mounted alongside this bar; this component owns the
 *          DISPOSITION verbs (Bills / trash / estimator / open card /
 *          open job) — every one a thin reuse of an existing,
 *          battle-tested handler. No new backend.
 *
 * ROLE IN ARCHITECTURE: Frame (hooks + guard + JSX only, change #269 D4
 *   500-line split) over ./smart-action-bar/*: the runtime hook owns every
 *   mutation, ActionButton/BillConfirmReject own presentation.
 * NON-RESPONSIBILITIES: Mutation logic (smart-action-bar/
 *   useSmartActionBarRuntime.ts); per-kind styling (smart-action-bar/
 *   ActionButton.tsx, BillConfirmReject.tsx); the Junk/Estimator split
 *   controls (JunkButton.tsx / EstimatorAllocator.tsx, mounted verbatim).
 * RELATED:
 *   - ./smart-action-bar/useSmartActionBarRuntime.ts : the resolver + verbs
 *   - ./smart-action-bar/ActionButton.tsx           : per-kind pill button
 *   - ./smart-action-bar/BillConfirmReject.tsx       : financial-in primary
 *   - ./EstimatorAllocator.tsx                       : quote_request primary
 *   - ../hooks/useEmails.ts                          : bulkAction / createAutoJunkRule
 */

import { useRenderTiming } from '@quotech/ui'

import { type Email } from '../hooks/useEmails'
import { EstimatorAllocator } from './EstimatorAllocator'
import { JunkButton } from './JunkButton'
import { hasHeaderActions, type HeaderActionKind } from '../lib/headerActions'
import {
  ActionButton,
  BillConfirmReject,
  useSmartActionBarRuntime,
} from './smart-action-bar'

interface SmartActionBarProps {
  email: Email
  /** Whole-header decision mode renders only a true confirm/reject primary.
   *  Disposition verbs (Done/Card/Junk) live in command/overflow once. */
  variant?: 'all' | 'decision'
  /** Refetch the list + the open email after a disposition mutates. */
  onRefetch?: () => void
  /**
   * Operator (2026-06-17): "I clicked Junk yet it did not vanish and go
   * to the next email." A TERMINAL disposition (Junk / Done / Bin /
   * auto-trash) removes the email from the inbox, so the reader must drop
   * it and advance to the next message — not just refetch and keep showing
   * the gone email. When provided, the host owns that remove+advance
   * (mirrors `handleBinRow`); when omitted we fall back to `onRefetch`.
   */
  onResolved?: (emailId: string) => void
  /**
   * UAT P2-b — when provided, "Open card" opens the allocated card
   * IN PLACE (the parent mounts a CardPeekDrawer) instead of page-
   * navigating to `/kanban/<board>?card=<id>` and losing the email
   * context. Omitted → legacy deep-link navigation (older mounts).
   */
  onOpenCard?: () => void
  /**
   * Operator (2026-06-15) — when provided, clicking the "Bill" chip (and
   * confirming ✓) pops the source invoice document open in the right
   * drawer (the parent mounts a BillDocDrawer), so the operator sees the
   * subcontractor/supplier invoice beside the email. Omitted → the Bill
   * chip is a static badge (older mounts).
   */
  onOpenBill?: () => void
}

export function SmartActionBar({
  email,
  variant = 'all',
  onRefetch,
  onResolved,
  onOpenCard,
  onOpenBill,
}: SmartActionBarProps) {
  useRenderTiming('SmartActionBar', { deps: [email?.id] })
  const { visibleModel, emailDone, busyKind, notBillBusy, run, markNotBill } =
    useSmartActionBarRuntime({ email, variant, onRefetch, onResolved, onOpenCard, onOpenBill })

  if (!hasHeaderActions(visibleModel)) return null

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="email-smart-action-bar"
    >
      {visibleModel.primary === 'allocate_estimator' ? (
        // EstimatorAllocator is a self-contained primary affordance
        // (button + teammate picker + SMTP hand-off).
        <EstimatorAllocator emailId={email.id} onAllocated={onRefetch} />
      ) : visibleModel.primary === 'send_to_bills' ? (
        // Operator (2026-06-03): financial-in mail should read as an
        // auto-classified BILL with a tick/cross — ✓ confirms (files it
        // to Bills + AR/AP), ✗ says "not a bill" — rather than a manual
        // "Send to Bills" button. Mirrors the list rail's AI ✓/✗.
        <BillConfirmReject
          busyConfirm={busyKind === 'send_to_bills'}
          busyReject={notBillBusy}
          disabled={busyKind != null && busyKind !== 'send_to_bills'}
          onConfirm={() => void run('send_to_bills')}
          onReject={() => void markNotBill()}
          onViewBill={onOpenBill}
        />
      ) : visibleModel.primary === 'mark_junk' ? (
        // Junk is a split control (bin now + retro/prospective options).
        <JunkButton email={email} onRefetch={onRefetch} onResolved={onResolved} disabled={busyKind != null} variant="primary" />
      ) : (
        visibleModel.primary && (
          <ActionButton
            kind={visibleModel.primary}
            variant="primary"
            done={emailDone}
            busy={busyKind === visibleModel.primary}
            disabled={busyKind != null && busyKind !== visibleModel.primary}
            onClick={() => void run(visibleModel.primary as HeaderActionKind)}
          />
        )
      )}

      {visibleModel.secondary.map((kind) =>
        // Junk is a split control (bin now + retro/prospective options),
        // not a single-shot verb — so it owns its own component and
        // mutation handlers rather than routing through `run`.
        kind === 'mark_junk' ? (
          <JunkButton
            key={kind}
            email={email}
            onRefetch={onRefetch}
            onResolved={onResolved}
            disabled={busyKind != null}
            variant="secondary"
          />
        ) : (
          // `model.secondary` is `SecondaryActionKind[]` which already
          // excludes `allocate_estimator` (primary-only affordance), so no
          // guard is needed here — the resolver never tails it.
          <ActionButton
            key={kind}
            kind={kind}
            variant="secondary"
            done={emailDone}
            busy={busyKind === kind}
            disabled={busyKind != null && busyKind !== kind}
            onClick={() => void run(kind)}
          />
        ),
      )}
    </div>
  )
}
